import React, { CSSProperties, useContext, useMemo } from "react";
import styled, { useTheme } from "styled-components";
import { TradingTerminalTabs } from "@shared/features/tradingTerminal/common/types";
import SmartTradeForm from "features/tradingTerminal/SmartTrade/SmartTradeForm";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import Summary from "features/tradingTerminal/Summary/Summary";
import TradingTerminalChart from "features/tradingTerminal/common/TradingTerminalChart";
import {
  MOBILE_TRADING_TERMINAL_PADDING,
  TRADING_TERMINAL_PADDING,
} from "@shared/features/tradingTerminal/common/constants";
import { useDisablePageScroll, useBottomNavigator } from "utils/hooks/misc";
import useMeasure from "utils/hooks/useMeasure";
import { BOTTOM_SUMMARY_CLASS } from "common/constants.ts";
import { useTelegramWebAppBottomPadding } from "utils/hooks/useTelegramWebApp.ts";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { NEW_LOOK_PAGE_GUTTER_VAR } from "common/NewLook";
import useFillRemainingHeight from "features/tradingTerminal/common/useFillRemainingHeight.ts";

const NewTradingTerminal = () => {
  useDisablePageScroll();
  const { tab, isMobile } = useContext(TradingTerminalContext)!;
  useMeasure<HTMLDivElement>();
  const telegramBottomPadding = useTelegramWebAppBottomPadding();
  const { isNewLook } = useTheme();

  const { baseShouldDisplay: isBottomNavigator } = useBottomNavigator();
  // New look (desktop): the page intro sits above the terminal, so the
  // terminal fills only the remaining height of the canvas.
  const isNewLookDesktop = isNewLook && !isBottomNavigator;
  const { ref: fillRef, offset: introOffset } =
    useFillRemainingHeight<HTMLDivElement>(isNewLookDesktop);
  const padding = isMobile
    ? MOBILE_TRADING_TERMINAL_PADDING
    : TRADING_TERMINAL_PADDING;

  const style = useMemo(() => {
    const style: CSSProperties = {
      padding: isBottomNavigator ? 0 : padding,
    };
    if (isNewLookDesktop) {
      style.padding = `16px ${NEW_LOOK_PAGE_GUTTER_VAR} 20px`;
      style.height = `calc(100% - ${introOffset}px)`;
      style.minHeight = 0;
    }
    if (IS_TELEGRAM_WEB_APP) {
      style.paddingBottom = telegramBottomPadding;
    }
    return style;
  }, [
    isBottomNavigator,
    padding,
    telegramBottomPadding,
    isNewLookDesktop,
    introOffset,
  ]);

  const summaryStyle = useMemo(() => {
    const style: CSSProperties = {
      marginTop: isBottomNavigator ? 0 : padding,
    };
    if (isNewLookDesktop) style.marginTop = 0;
    return style;
  }, [isBottomNavigator, padding, isNewLookDesktop]);

  return (
    <ContentWrapper style={style} ref={fillRef}>
      <FormAndChartWrapper $isMobile={isMobile}>
        <FormAndSummaryWrapper
          $isMobile={isMobile}
          $isTicket={isNewLookDesktop}
        >
          <FormWrapper
            $isBottomNavigator={isBottomNavigator}
            $isTicket={isNewLookDesktop}
          >
            <SmartTradeForm />
          </FormWrapper>
          <SummaryWrapper
            style={summaryStyle}
            $isBottomNavigator={isBottomNavigator}
            $isTicket={isNewLookDesktop}
            className={BOTTOM_SUMMARY_CLASS}
          >
            <Summary isOrder={tab === TradingTerminalTabs.SIMPLE_ORDER} />
          </SummaryWrapper>
        </FormAndSummaryWrapper>
        {!isMobile && (
          <TabsViewChartWrapper>
            <TradingTerminalChart isCard={true} />
          </TabsViewChartWrapper>
        )}
      </FormAndChartWrapper>
    </ContentWrapper>
  );
};

export default NewTradingTerminal;

const FormWrapper = styled.div<{
  $isBottomNavigator?: boolean;
  $isTicket?: boolean;
}>`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow-y: hidden;
  overflow-x: hidden;
  ${(p) =>
    p.$isBottomNavigator
      ? `
      flex: 1;
    `
      : `
     flex: 0 1 auto;
     border: ${p.theme.borders.medium};
     background: ${p.theme.colors.cardBgColor};
  `};
  ${(p) =>
    p.theme.isNewLook &&
    !p.$isBottomNavigator &&
    `
    border: ${p.theme.borders.panel};
    border-radius: ${p.theme.radii.card}px;
    box-shadow: ${p.theme.shadows.panel};
  `}
  ${(p) =>
    p.$isTicket &&
    `
    min-height: 0;
    border: none;
    border-radius: 0;
    box-shadow: none;
  `}
`;
const ContentWrapper = styled.div`
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  width: 100%;
  min-height: 100%;
  height: 1px;
`;
const FormAndSummaryWrapper = styled.div<{
  $isMobile?: boolean;
  $isTicket?: boolean;
}>`
  display: flex;
  flex-direction: column;
  max-width: ${(p) => (p.$isMobile ? 500 : 365)}px;
  width: 100%;
  ${(p) => p.theme.isNewLook && !p.$isMobile && `max-width: 380px;`}
  ${(p) =>
    p.$isMobile &&
    `
    justify-content: space-between;
  `};
  ${(p) =>
    p.$isTicket &&
    `
    align-self: flex-start;
    max-height: 100%;
    min-height: 0;
    overflow: hidden;
    border: ${p.theme.borders.panel};
    border-radius: ${p.theme.radii.card}px;
    box-shadow: ${p.theme.shadows.panel};
    background: ${p.theme.colors.cardBgColor};
  `}
`;
const SummaryWrapper = styled.div<{
  $isBottomNavigator?: boolean;
  $isTicket?: boolean;
}>`
  border-radius: ${(p) => (p.$isBottomNavigator ? 0 : 8)}px;
  border: ${(p) => (p.$isBottomNavigator ? "none" : p.theme.borders.medium)};
  padding: 12px;
  ${(p) =>
    p.$isBottomNavigator
      ? `border-top: ${p.theme.borders.medium};`
      : `background: ${p.theme.colors.cardBgColor};`};
  ${(p) =>
    p.theme.isNewLook &&
    (p.$isBottomNavigator
      ? `border-top: ${p.theme.borders.lightest};`
      : `
    border: ${p.theme.borders.panel};
    border-radius: ${p.theme.radii.card}px;
    box-shadow: ${p.theme.shadows.panel};
    padding: 20px;
  `)}
  ${(p) =>
    p.$isTicket &&
    `
    flex: none;
    border: none;
    border-top: ${p.theme.borders.card};
    border-radius: 0;
    box-shadow: none;
    padding: 16px;
  `}
`;
const FormAndChartWrapper = styled.div<{
  $isMobile?: boolean;
}>`
  height: calc(100% - 40px);
  display: flex;
  justify-content: center;
  column-gap: 20px;
  ${(p) =>
    p.$isMobile &&
    `
    height: 100%;
  `};
  ${(p) =>
    p.theme.isNewLook &&
    !p.$isMobile &&
    `
    height: 100%;
    column-gap: 16px;
  `}
`;
const TabsViewChartWrapper = styled.div`
  border: ${(p) => p.theme.borders.medium};
  border-radius: 8px;
  padding: 8px 0 4px 0;
  flex: 1;
  background: ${(p) => p.theme.colors.cardBgColor};
  ${(p) =>
    p.theme.isNewLook &&
    `
    min-width: 0;
    overflow: hidden;
    border: ${p.theme.borders.panel};
    border-radius: ${p.theme.radii.card}px;
    box-shadow: ${p.theme.shadows.panel};
  `}
`;
